import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosError, AxiosResponse } from "axios";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog } from "primereact/confirmdialog";
import { classNames } from "primereact/utils";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { TextInput } from "../../components/TextInput/TextInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { CheckboxInput } from "../../components/CheckboxInput/CheckboxInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { AutocompleteInput } from "../../components/AutocompleteInput/AutocompleteInput";
import { Request, RequestType } from "../../models/Request";
import { Partner } from "../../models/Partner";
import { Product, ProductType } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { RoleType } from "../../models/Role";
import { City } from "../../models/City";
import { Disposition } from "../../models/Disposition";
import { User } from "../../models/User";
import { Utility } from "../../core/Utility";
import { VehicleTypeOptions } from "../../mock/StaticData";
import { activeUser } from "../../services/AuthService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllUsers } from "../../services/UserService";
import {
  getAllRegistrationNumbers,
  getAllDriverNames,
  getDispositionsByRequest,
  getOneDisposition,
  addNewDisposition,
  updateOneDisposition,
  deleteOneDisposition,
  getOneRequest,
} from "../../services/NewVersionService";
import "./NewReproRequestDetailsPage.scss";

export const NewReproRequestDetailsPage = () => {
  const { t } = useTranslation();

  const [partners, setPartners] = useState<Partner[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const [request, setRequest] = useState<Request>();
  const [dispositionsAtRequest, setDispositionsAtRequest] = useState<
    Disposition[]
  >([]);
  const [disposition, setDisposition] = useState<Disposition>({
    id: "",
    dateFrom: new Date(),
    sendEmail: false,
    loadingFinished: false,
    repromaterialDispositionProducts: [],
  });

  const [dispositionAmounts, setDispositionAmounts] = useState<
    {
      dispositionProduct: string;
      requestAmount: string;
      dispositionAmount: string;
    }[]
  >(() => {
    if (request?.repromaterialRequestProducts && products.length > 0) {
      return request.repromaterialRequestProducts.map((productItem) => ({
        dispositionProduct: productItem.id,
        requestAmount: productItem.amount ? productItem.amount.toString() : "0",
        dispositionAmount: "",
      }));
    }
    return [];
  });

  const [allDispositionsByRequest, setAllDispositionsByRequest] = useState<
    Disposition[]
  >([]);
  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [dispositionToDelete, setDispositionToDelete] = useState<string>("");
  const [emailsOfWarehouseman, setEmailsOfWarehouseman] = useState<string[]>(
    [],
  );
  const [emailsSugestions, setEmailsSugestions] = useState<string[]>([]);
  const [driverNames, setDriverNames] = useState<string[]>([]);
  const [driverNamesSugestions, setDriverNamesSugestions] = useState<string[]>(
    [],
  );
  const [registrationNumbers, setRegistrationNumbers] = useState<string[]>([]);
  const [registrationNumbersSugestions, setRegistrationnumbersSugestions] =
    useState<string[]>([]);
  const [foreignRegistrationNumber, setForeignRegistrationNumber] =
    useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    registrationNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dispositionNumberFormatted: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    driverName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    price: { value: null, matchMode: FilterMatchMode.CONTAINS },
    loadingFinished: { value: null, matchMode: FilterMatchMode.EQUALS },
    inputLocation: { value: null, matchMode: FilterMatchMode.CONTAINS },
    outputLocation: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const { id } = useParams();

  const toast = useRef(null);

  useEffect(() => {
    setProcessing(true);
    const unwrap = (resp: any): any[] => resp?.data?.result ?? resp?.data ?? [];
    Promise.all([
      getDispositionsByRequest(id as string),
      getAllDriverNames(),
      getAllRegistrationNumbers(),
      getAllPartners(),
      getAllWarehouses(),
      getAllProducts(),
      getAllUsers(),
    ])
      .then((returnValues) => {
        const allDispByRequest = unwrap(returnValues[0]);
        setAllDispositionsByRequest(allDispByRequest);

        const allDriverOptions = unwrap(returnValues[1]);
        const allRegistrationNumbersOptions = unwrap(returnValues[2]);

        const pa: Partner[] = unwrap(returnValues[3]);
        const wh: Warehouse[] = unwrap(returnValues[4]);
        const pr: Product[] = unwrap(returnValues[5]);
        const us: User[] = unwrap(returnValues[6]);

        setPartners(pa);
        setWarehouses(wh);
        setProducts(pr);

        let newDispositions: Disposition[] = [];
        let maxDispositionId: number = 1;

        allDispByRequest.map((dispItem: any) => {
          if (dispItem.request?.id === id) {
            newDispositions.push(dispItem);
            if (
              dispItem?.dispositionIdAtRequest &&
              dispItem?.dispositionIdAtRequest >= maxDispositionId
            ) {
              maxDispositionId = dispItem.dispositionIdAtRequest + 1; //set new dispositionAtRequestId
            }
          }
        });
        setDisposition((disposition) => ({
          ...disposition,
          dispositionIdAtRequest: maxDispositionId,
        }));
        setDispositionsAtRequest(newDispositions);

        //set email sugestions
        const newEmailSugestions: string[] = [];
        us.forEach((item) => {
          //if (item?.role?.name === 'Magacioner')
          if (item?.email) newEmailSugestions.push(item.email);
        });
        pa.forEach((onePartner) => {
          if (onePartner?.email && onePartner?.email !== "") {
            newEmailSugestions.push(onePartner.email);
          }
        });
        setEmailsOfWarehouseman(newEmailSugestions);

        //set unique driver name sugestions
        let newDriverNameSugestions: string[] = [];
        allDriverOptions.map((item: any) =>
          newDriverNameSugestions.push(item?.driverName as string),
        ); //@ts-ignore
        let uniqDriverNames = [...new Set(newDriverNameSugestions)];
        setDriverNames(uniqDriverNames);

        //set registration number sugestions
        let newRegistrationNumberSugestions: string[] = [];
        allRegistrationNumbersOptions.map((item: any) =>
          newRegistrationNumberSugestions.push(
            (item?.registrationNumber as string).toUpperCase(),
          ),
        );
        let uniqRegistrationNumbers = [
          //@ts-ignore
          ...new Set(newRegistrationNumberSugestions),
        ];
        setRegistrationNumbers(uniqRegistrationNumbers);
      })
      .catch((error: AxiosError) => {
        console.log(error);
      })
      .finally(() => setProcessing(false));
  }, []);

  useEffect(() => {
    if (!id) return;

    getOneRequest(id as string)
      .then((resp: AxiosResponse) => {
        const req = resp?.data?.result;
        if (req) setRequest(req);
      })
      .catch((e) => console.log("getOneRequest failed", e));
  }, [id]);

  useEffect(() => {
    if (request?.repromaterialRequestProducts && products.length > 0) {
      loadRemainingAmountByProduct();
    }
  }, [allDispositionsByRequest, request, products]);

  const loadRemainingAmountByProduct = () => {
    let copyOfDispositionAmounts: {
      dispositionProduct: string;
      requestAmount: string;
      dispositionAmount: string;
    }[] = [];
    request?.repromaterialRequestProducts?.map((productItem: Product) => {
      let maxAmountByProduct = parseFloat(productItem?.amount as string);
      let newDispositions: Disposition[] = [];
      allDispositionsByRequest.map((dispItem) => {
        if (dispItem.request?.id === id) {
          newDispositions.push(dispItem);
        }
      });
      for (const oneDisposition of newDispositions) {
        const oneDispositionProducts = JSON.parse(
          oneDisposition.repromaterialDispositionProductsJSON as string,
        );
        for (const oneDispositionProduct of oneDispositionProducts) {
          if (oneDispositionProduct.id === productItem.id) {
            maxAmountByProduct -=
              oneDispositionProduct.amount != ""
                ? parseFloat(oneDispositionProduct.amount as string)
                : 0;
          }
        }
      }
      copyOfDispositionAmounts.push({
        dispositionProduct: productItem.name,
        requestAmount: maxAmountByProduct.toString(),
        dispositionAmount: "",
      });
    });
    setDispositionAmounts(copyOfDispositionAmounts);
  };

  const accept = () => {
    setProcessing(true);
    deleteOneDisposition(dispositionToDelete)
      .then(() => {
        setDispositionToDelete("");
        return getDispositionsByRequest(id as string);
      })
      .then((allNewDispByRequest) => {
        setAllDispositionsByRequest(allNewDispByRequest.data.result);
        //@ts-ignore
        toast.current.show({
          severity: "success",
          detail: t(`Dispozicija je obrisana`),
          life: 3000,
        });
      })
      .catch((error: AxiosError<any>) => {
        //@ts-ignore
        toast.current.show({
          severity: "error",
          detail:
            error?.response?.data?.message ??
            t(`Nije moguće obrisati dispoziciju`),
          life: 4000,
        });
      })
      .finally(() => setProcessing(false));
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const filterEmails = function (e: any) {
    let results = emailsOfWarehouseman.filter((email) => {
      return email.toLowerCase().startsWith(e.query.toLowerCase());
    });
    setEmailsSugestions(results);
  };

  const filterDriverNames = function (e: any) {
    let results = driverNames.filter((driverName) => {
      return driverName.toLowerCase().startsWith(e.query.toLowerCase());
    });
    setDriverNamesSugestions(results);
  };

  const filterRegistrationNumbers = function (e: any) {
    let results = registrationNumbers.filter((registrationNumber) => {
      return registrationNumber.toLowerCase().startsWith(e.query.toLowerCase());
    });
    setRegistrationnumbersSugestions(results);
  };

  const handleNewDocumentOfReceive = (prop: string, value: any) => {
    const newDisposition = Utility.cloneDeep(disposition);
    newDisposition[prop] = value;
    setDisposition(newDisposition);
  };

  const handleNewRegistrationNumber = (prop: string, value: any) => {
    const newDisposition = Utility.cloneDeep(disposition);
    newDisposition[prop] = value.toUpperCase();
    setDisposition(newDisposition);
  };

  const handleTransportationPartnerChange = (prop: string, value: any) => {
    let newDisposition = cloneDeep(disposition);
    newDisposition.transportationPartner = partners.find(
      (item) => item.id == value,
    );
    setDisposition(newDisposition);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    //import, relocation, export
    let newDisposition = cloneDeep(disposition);
    newDisposition.inputWarehouse = warehouses.find((item) => item.id == value);
    newDisposition.inputLocation = warehouses.find(
      (item) => item.id == value,
    )?.name;
    setDisposition(newDisposition);
  };

  const handleNewDispositionAmount = (prop: string, value: any) => {
    const newDispositionAmounts = Utility.cloneDeep(dispositionAmounts);
    newDispositionAmounts[prop].dispositionAmount = value;
    setDispositionAmounts(newDispositionAmounts);
  };

  const handleForeignRegistrationNumber = () => {
    setForeignRegistrationNumber(!foreignRegistrationNumber);
  };

  const onSubmit = () => {
    for (const dispositionAmount of dispositionAmounts) {
      if (
        parseFloat(dispositionAmount.dispositionAmount) >
        parseFloat(dispositionAmount.requestAmount) + 0.001
      ) {
        //@ts-ignore
        toast.current.show({
          severity: "error",
          detail: `${t("Količina dispozicije za robu")} ${products.find((item) => item.id === dispositionAmount.dispositionProduct)?.name} ${t("je veća od ukupne količine zahteva umanjene za već kreiranje dispozicije. Proverite unetu količinu")}`,
          sticky: true,
        });
        return;
      }
    }

    //add dispostion amounts
    let copyOfDisposition = cloneDeep(disposition);
    copyOfDisposition.repromaterialDispositionProducts = [];
    dispositionAmounts.map((itemAmount) => {
      copyOfDisposition.repromaterialDispositionProducts?.push({
        id: itemAmount.dispositionProduct,
        type: ProductType.RawMaterial,
        code: "",
        name: itemAmount.dispositionProduct,
        amount: itemAmount.dispositionAmount,
        category: {
          id: "",
          type: ProductType.RawMaterial,
          name: "",
        },
      });
    });

    //format multiple email
    let multipleEmail: string = "";
    if (copyOfDisposition.email !== "") {
      copyOfDisposition.email?.map((oneEmail: any) => {
        multipleEmail += oneEmail + ";";
      });
    }
    if (multipleEmail.length > 0) {
      multipleEmail = multipleEmail.slice(0, -1);
    }
    copyOfDisposition.email = multipleEmail;

    dispositionsAtRequest.push(copyOfDisposition);
    let dispositionToSave = cloneDeep(copyOfDisposition);
    if (copyOfDisposition.id) {
      updateOneDisposition(copyOfDisposition.id, dispositionToSave).then(() => {
        setProcessing(true);
        getDispositionsByRequest(id as string)
          .then((allNewDispByRequest) => {
            setAllDispositionsByRequest(allNewDispByRequest.data.result);
          })
          .finally(() => setProcessing(false));
      });
    } else {
      dispositionToSave.request = { id: id! };
      let copyOfDispositionToSave: any = cloneDeep(dispositionToSave);
      copyOfDispositionToSave.id = undefined;
      addNewDisposition(copyOfDispositionToSave).then(() => {
        setProcessing(true);
        getDispositionsByRequest(id as string)
          .then((allNewDispByRequest) => {
            setAllDispositionsByRequest(allNewDispByRequest.data.result);
          })
          .finally(() => setProcessing(false));
      });
    }

    let maxDispositionId: number = 1;
    dispositionsAtRequest.map((dispItem) => {
      if (
        dispItem.dispositionIdAtRequest &&
        dispItem.dispositionIdAtRequest >= maxDispositionId
      ) {
        maxDispositionId = dispItem.dispositionIdAtRequest + 1;
      }
    });

    setDisposition({
      id: "",
      dispositionIdAtRequest: maxDispositionId,
      dateFrom: undefined,
      dateTo: undefined,
      vehicleTypeId: "",
      transportationPartner: {} as Partner,
      inputLocation: "",
      outputLocation: "",
      inputWarehouse: undefined,
      outputCity: undefined,
      registrationNumber: "",
      price: 0,
      tollPrice: 0,
      driverName: "",
      email: "",
      loadingFinished: false,
    });

    //reset disposition amounts data
    let copyOfDispositionAmounts: {
      dispositionProduct: string;
      requestAmount: string;
      dispositionAmount: string;
    }[] = [];
    for (const dispositionAmountItem of dispositionAmounts) {
      copyOfDispositionAmounts.push({
        dispositionProduct: dispositionAmountItem.dispositionProduct,
        requestAmount: (
          parseFloat(dispositionAmountItem.requestAmount) -
          (dispositionAmountItem.dispositionAmount != ""
            ? parseFloat(dispositionAmountItem.dispositionAmount)
            : 0)
        ).toString(),
        dispositionAmount: "",
      });
    }
    setDispositionAmounts(copyOfDispositionAmounts);

    //@ts-ignore
    toast.current.show({
      severity: "success",
      detail: t(`Uspešno dodati novi podaci`),
      life: 3000,
    });
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetForm = () => {
    setDisposition({
      id: "",
      registrationNumber: "",
      driverName: "",
      price: 0,
    });
    //reset disposition amounts
    let copyOfDispositionAmounts = cloneDeep(dispositionAmounts);
    dispositionAmounts.map((oneDispositionAmount: any, index: number) => {
      copyOfDispositionAmounts[index].dispositionAmount = "";
    });
    setDispositionAmounts(copyOfDispositionAmounts);
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
  addButtonSaveStyle.border = "none";
  addButtonSaveStyle.margin = "0 .5rem";

  let addButtonNewPartnerStyle: React.CSSProperties = {};
  addButtonNewPartnerStyle.background = "rgba(21, 115, 71, 1)";
  addButtonNewPartnerStyle.border = "none";

  let addButtonResetStyle: React.CSSProperties = {};
  addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
  addButtonResetStyle.border = "none";

  let addButtonNewCityStyle: React.CSSProperties = {};
  addButtonNewCityStyle.background = "rgba(21, 115, 71, 1)";
  addButtonNewCityStyle.border = "none";

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <div className="header-table-container">
        <div>
          <i className="bi bi-truck header-title-icon"></i>
          <span className="header-title-content">{`${t("Dispozicije za zahtev")}`}</span>
        </div>
        <div className="search-container">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Globalna pretraga..."
            />
          </span>
        </div>
      </div>
    );
  };

  const formatDate = (rowData: Disposition) => {
    return (
      <span>{Utility.formatDate(new Date(rowData.dateFrom as Date))}</span>
    );
  };

  const formatLoadingFinished = (rowData: Disposition) => {
    return <span>{rowData.loadingFinished === true ? "Da" : "Ne"}</span>;
  };

  const loadingFinishedOptions = [
    { label: t("Da"), value: true },
    { label: t("Ne"), value: false },
  ];

  const loadingFinishedItemTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option.label}</span>
      </div>
    );
  };

  const loadingFinishedRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={loadingFinishedOptions}
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        itemTemplate={loadingFinishedItemTemplate}
        placeholder="Izaberite"
        className="p-column-filter"
        showClear
      />
    );
  };

  const formatVehicleType = (rowData: Disposition) => {
    return (
      <div>
        {rowData.vehicleTypeId == "1"
          ? "Kamion"
          : rowData.vehicleTypeId == "2"
            ? "Kamion sa prikolicom"
            : rowData.vehicleTypeId == "3"
              ? "Kombi"
              : rowData.vehicleTypeId == "4"
                ? "Traktor"
                : rowData.vehicleTypeId == "5"
                  ? "Vagon"
                  : rowData.vehicleTypeId == "6"
                    ? "Automobil"
                    : ""}
      </div>
    );
  };

  const formatOptions = (rowData: Disposition) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-files"
          onButtonClick={async () => {
            const id = rowData.id ?? "";
            const dispositionCopy = allDispositionsByRequest.find(
              (disposition) => disposition.id === id,
            );
            let maxDispositionId: number = 2;
            dispositionsAtRequest.map((dispItem) => {
              if (
                dispItem.dispositionIdAtRequest &&
                dispItem.dispositionIdAtRequest >= maxDispositionId
              ) {
                maxDispositionId = dispItem.dispositionIdAtRequest + 1;
              }
            });
            setDisposition({
              id: "",
              dispositionIdAtRequest: maxDispositionId,
              dateFrom:
                new Date(dispositionCopy?.dateFrom as Date) ?? undefined,
              dateTo: new Date(dispositionCopy?.dateTo as Date) ?? undefined,
              vehicleTypeId: dispositionCopy?.vehicleTypeId ?? "",
              transportationPartner:
                cloneDeep(dispositionCopy?.transportationPartner) ??
                ({} as Partner),
              inputLocation: dispositionCopy?.inputLocation ?? "",
              outputLocation: dispositionCopy?.outputLocation ?? "",
              inputWarehouse:
                dispositionCopy?.inputWarehouse ?? ({} as Warehouse),
              outputCity: dispositionCopy?.outputCity ?? ({} as City),
              registrationNumber: dispositionCopy?.registrationNumber ?? "",
              price: dispositionCopy?.price ?? undefined,
              tollPrice: dispositionCopy?.tollPrice ?? undefined,
              driverName: dispositionCopy?.driverName ?? "",
              email: [],
              loadingFinished: false,
            });
            //reset disposition amounts
            let copyOfDispositionAmounts = cloneDeep(dispositionAmounts);
            dispositionAmounts.map(
              (oneDispositionAmount: any, index: number) => {
                copyOfDispositionAmounts[index].dispositionAmount = "";
              },
            );
            setDispositionAmounts(copyOfDispositionAmounts);
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        <ButtonInput
          addStyle={{
            border: "none",
            background: "#198754",
            margin: "0 0.6rem",
          }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => {
            const id = rowData?.id ?? "";
            getOneDisposition(id).then((response: AxiosResponse) => {
              const disposition = response.data.result;
              if (disposition) {
                if (disposition.dateFrom)
                  disposition.dateFrom = new Date(disposition.dateFrom);
                if (disposition.dateTo)
                  disposition.dateTo = new Date(disposition.dateTo);
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                if (disposition?.email && !Array.isArray(disposition?.email))
                  disposition.email = disposition?.email.split(";") ?? [];
                setDisposition(cloneDeep(disposition));
                //set selected disposition amounts
                let copyOfDispositionAmounts: {
                  dispositionProduct: string;
                  requestAmount: string;
                  dispositionAmount: string;
                }[] = [];
                request?.repromaterialRequestProducts?.map(
                  (productItem: Product) => {
                    let maxAmountByProduct = parseFloat(
                      productItem?.amount as string,
                    );
                    for (const oneDisposition of allDispositionsByRequest) {
                      const oneDispositionProducts = JSON.parse(
                        (oneDisposition.repromaterialDispositionProductsJSON as string) ??
                          "[]",
                      );
                      for (const oneDispositionProduct of oneDispositionProducts) {
                        if (oneDispositionProduct.id === productItem.id) {
                          maxAmountByProduct -=
                            oneDispositionProduct.amount != ""
                              ? parseFloat(
                                  oneDispositionProduct.amount as string,
                                )
                              : 0;
                        }
                      }
                    }
                    copyOfDispositionAmounts.push({
                      dispositionProduct: productItem.name,
                      requestAmount: maxAmountByProduct.toString(),
                      dispositionAmount: "",
                    });
                  },
                );
                const allDispositionAmounts = JSON.parse(
                  disposition.repromaterialDispositionProductsJSON as string,
                );
                allDispositionAmounts.map(
                  (oneDispositionAmount: any, index: number) => {
                    let newDispositionAmount: number =
                      parseFloat(
                        copyOfDispositionAmounts[index].requestAmount,
                      ) +
                      (oneDispositionAmount.amount != ""
                        ? parseFloat(oneDispositionAmount.amount)
                        : 0);
                    copyOfDispositionAmounts[index].requestAmount =
                      newDispositionAmount.toString();
                    copyOfDispositionAmounts[index].dispositionAmount =
                      oneDispositionAmount.amount;
                  },
                );
                setDispositionAmounts(copyOfDispositionAmounts);
              }
            });
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-trash"
          onButtonClick={async () => {
            const id = rowData?.id ?? "";
            getOneDisposition(id).then((response: AxiosResponse) => {
              const disposition = response.data.result;
              if (disposition?.loadingFinished === true) {
                //@ts-ignore
                toast.current.show({
                  severity: "error",
                  detail: t(
                    `Nije moguće brisati dispozicije koje su zaključene`,
                  ),
                  life: 3000,
                });
              } else {
                setDispositionToDelete(id);
                setDeleteConfirmVisible(true);
              }
            });
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
      </div>
    );
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="request-details-wrapper">
        <div className="request-details-data">
          <div
            className={classNames(
              "request-details-header",
              {
                "request-details-header-import":
                  request?.type === RequestType.Import,
              },
              {
                "request-details-header-export":
                  request?.type === RequestType.Export,
              },
              {
                "request-details-header-relocation":
                  request?.type === RequestType.Relocation,
              },
            )}
          >
            <i className="bi bi-truck"></i> {t("Zahtev ")}
            {`${request?.requestNumberFormatted ?? ""}`}
          </div>
          <table
            className={classNames(
              "request-details-table",
              {
                "request-details-table-import":
                  request?.type === RequestType.Import,
              },
              {
                "request-details-table-export":
                  request?.type === RequestType.Export,
              },
              {
                "request-details-table-relocation":
                  request?.type === RequestType.Relocation,
              },
            )}
          >
            <tr>
              <td
                className={classNames(
                  "request-details-data",
                  {
                    "request-details-data-import":
                      request?.type === RequestType.Import,
                  },
                  {
                    "request-details-data-export":
                      request?.type === RequestType.Export,
                  },
                  {
                    "request-details-data-relocation":
                      request?.type === RequestType.Relocation,
                  },
                )}
              >
                {request?.type === RequestType.Export
                  ? `${t("Kupac robe")}`
                  : request?.type === RequestType.Import
                    ? `${t("Dobavljač robe")}`
                    : `${t("Vlasnik robe")}`}
              </td>
              <td
                className={classNames(
                  "request-details-data",
                  {
                    "request-details-data-import":
                      request?.type === RequestType.Import,
                  },
                  {
                    "request-details-data-export":
                      request?.type === RequestType.Export,
                  },
                  {
                    "request-details-data-relocation":
                      request?.type === RequestType.Relocation,
                  },
                )}
              >
                {request?.partner?.name}
              </td>
              <td
                className={classNames(
                  "request-details-data",
                  {
                    "request-details-data-import":
                      request?.type === RequestType.Import,
                  },
                  {
                    "request-details-data-export":
                      request?.type === RequestType.Export,
                  },
                  {
                    "request-details-data-relocation":
                      request?.type === RequestType.Relocation,
                  },
                )}
              >
                {t("Trošak uračunat")}
              </td>
              <td
                className={classNames(
                  "request-details-data",
                  {
                    "request-details-data-import":
                      request?.type === RequestType.Import,
                  },
                  {
                    "request-details-data-export":
                      request?.type === RequestType.Export,
                  },
                  {
                    "request-details-data-relocation":
                      request?.type === RequestType.Relocation,
                  },
                )}
              >
                {request?.transportCost ? t("Da") : t("Ne")}
              </td>
            </tr>
            <tr>
              <td
                className={classNames(
                  "request-details-data",
                  {
                    "request-details-data-import":
                      request?.type === RequestType.Import,
                  },
                  {
                    "request-details-data-export":
                      request?.type === RequestType.Export,
                  },
                  {
                    "request-details-data-relocation":
                      request?.type === RequestType.Relocation,
                  },
                )}
              >
                {request?.type === RequestType.Import
                  ? t("Polazno mesto")
                  : request?.type === RequestType.Export
                    ? t("Prijemno mesto")
                    : t("Izlazno mesto")}
              </td>
              <td
                className={classNames(
                  "request-details-data",
                  {
                    "request-details-data-import":
                      request?.type === RequestType.Import,
                  },
                  {
                    "request-details-data-export":
                      request?.type === RequestType.Export,
                  },
                  {
                    "request-details-data-relocation":
                      request?.type === RequestType.Relocation,
                  },
                )}
              >
                {`${request?.place ?? ""}
                            ${request?.type === RequestType.Export ? (request?.address ? ", " : "") : ""}
                            ${request?.type === RequestType.Export ? (request?.address ?? "") : ""}`}
              </td>
              <td
                className={classNames(
                  "request-details-data",
                  {
                    "request-details-data-import":
                      request?.type === RequestType.Import,
                  },
                  {
                    "request-details-data-export":
                      request?.type === RequestType.Export,
                  },
                  {
                    "request-details-data-relocation":
                      request?.type === RequestType.Relocation,
                  },
                )}
              >
                {`${t("Napomena")}:`}
              </td>
              <td
                className={classNames(
                  "request-details-data",
                  {
                    "request-details-data-import":
                      request?.type === RequestType.Import,
                  },
                  {
                    "request-details-data-export":
                      request?.type === RequestType.Export,
                  },
                  {
                    "request-details-data-relocation":
                      request?.type === RequestType.Relocation,
                  },
                )}
              >
                {request?.note}
              </td>
            </tr>
          </table>
        </div>
        <div className="request-details-form">
          {dispositionAmounts?.map((productItem, index) => (
            <div className="request-details-form-row">
              <TextInput
                id="productName"
                value={
                  (products.find(
                    (item) => item.id === productItem.dispositionProduct,
                  )?.name as string) ?? ""
                }
                onValueChange={handleNewDocumentOfReceive}
                labelText={t("Roba")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled
              />
              <TextInput
                id="productRequestAmount"
                value={
                  !Number.isInteger(
                    parseFloat(productItem.requestAmount as string),
                  )
                    ? parseFloat(productItem.requestAmount as string).toFixed(2)
                    : (productItem.requestAmount as string)
                }
                onValueChange={handleNewDocumentOfReceive}
                labelText={t("Dostupna količina")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled
              />
              <TextInput
                id={index.toString()}
                value={(productItem.dispositionAmount as string) ?? ""}
                onValueChange={handleNewDispositionAmount}
                labelText={`${t("Količina dispozicije")} *`}
                placeholderText={t("Unesite količinu za dispoziciju")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: {
                    value: /^\d*\.?\d*$/,
                    message: "Please enter a number",
                  },
                }}
                disabled={disposition.loadingFinished === true}
              />
            </div>
          ))}
          <div className="request-details-form-row">
            <DateInput
              id="dateFrom"
              value={disposition.dateFrom as Date}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Datum od")} *`}
              placeholderText={t("Datum")}
              icon="bi bi-calendar-check"
              maxDate={disposition.dateTo}
              minDate={new Date()}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
            <DateInput
              id="dateTo"
              value={disposition.dateTo as Date}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Datum do")} *`}
              placeholderText={t("Datum")}
              icon="bi bi-calendar-check"
              minDate={disposition.dateFrom}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
            <DropdownInput
              id="transportationPartner"
              value={disposition.transportationPartner?.id as string}
              options={partners}
              optionValue="id"
              optionLabel="name"
              onValueChange={handleTransportationPartnerChange}
              filter={true}
              labelText={`${t("Prevoznik")} *`}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
          </div>
          <div className="request-details-form-row">
            <DropdownInput
              id="vehicleTypeId"
              value={disposition.vehicleTypeId as string}
              onValueChange={handleNewDocumentOfReceive}
              options={VehicleTypeOptions}
              filter={false}
              labelText={`${t("Tip vozila")} *`}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
            <div className="registration-tables-container">
              <div className="registration-tables">
                <AutocompleteInput
                  id="registrationNumber"
                  value={disposition?.registrationNumber as string}
                  sugestions={registrationNumbersSugestions}
                  completeMethod={filterRegistrationNumbers}
                  onValueChange={handleNewRegistrationNumber}
                  labelText={`${t("Registarski broj vozila")} *`}
                  placeholder={
                    (foreignRegistrationNumber === true
                      ? t("Upišite reg. broj vozila sa stranim tablicama")
                      : t(
                          "Upišite reg. br. u formatu AA 1234AA ili AA AAA12",
                        )) as string
                  }
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={
                    foreignRegistrationNumber === false
                      ? {
                          required: true,
                          pattern: {
                            value:
                              /^[A-Z]{2}\s([0-9]{3,4}[A-Z]{2}|[A-Z]{3}[0-9]{2})$/,
                            message:
                              "Unesite registarski broj u ispravnom formatu",
                          },
                        }
                      : {
                          required: true,
                          pattern: {
                            value: /.{2,}/,
                          },
                        }
                  }
                  disabled={disposition.loadingFinished === true}
                />
              </div>
              <div className="registration-tables-foreign">
                <CheckboxInput
                  id="foreignRegistrationNumber"
                  checked={foreignRegistrationNumber}
                  onValueChange={handleForeignRegistrationNumber}
                  labelText={t("Strane tablice")}
                  control={control}
                  errors={errors}
                />
              </div>
            </div>
            <AutocompleteInput
              id="driverName"
              value={disposition?.driverName as string}
              sugestions={driverNamesSugestions}
              completeMethod={filterDriverNames}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Ime vozača")} *`}
              placeholder={t("Upišite ime vozača") as string}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
          </div>
          <div className="request-details-form-row">
            <DropdownInput
              id="inputWarehouse"
              value={disposition.inputWarehouse?.id}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehouses.filter((item) => item.virtual !== true)}
              labelText={
                request?.type === RequestType.Export
                  ? `${t("Mesto utovara")} *`
                  : `${t("Mesto istovara")} *`
              }
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={disposition.loadingFinished === true}
            />
            <TextInput
              id="price"
              value={disposition.price as number}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Cena prevoza")} *`}
              placeholderText={t("Upišite cenu prevoza")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                required: true,
                pattern: {
                  value: /^\d+$/,
                  message: "Please enter a number",
                },
              }}
              disabled={disposition.loadingFinished === true}
            />
            <TextInput
              id="tollPrice"
              value={disposition.tollPrice as number}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("Putarina")}`}
              placeholderText={t("Upišite putarinu")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                pattern: {
                  value: /^\d+$/,
                  message: "Please enter a number",
                },
              }}
              disabled={disposition.loadingFinished === true}
            />
          </div>
          <div className="request-details-form-row">
            <AutocompleteInput
              id="email"
              value={disposition?.email as string}
              sugestions={emailsSugestions}
              multiple={true}
              completeMethod={filterEmails}
              onValueChange={handleNewDocumentOfReceive}
              labelText={`${t("E-mailovi magacionera")}`}
              placeholder={t("Upišite e-mailove magacionera") as string}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={disposition.loadingFinished === true}
            />
            <div className="send-email-checkbox">
              <CheckboxInput
                id="sendEmail"
                checked={disposition.sendEmail as boolean}
                onValueChange={handleNewDocumentOfReceive}
                labelText={t("Poslati mailove")}
                control={control}
                errors={errors}
              />
            </div>
            <RadioButtonInput
              id="loadingFinished"
              value={disposition.loadingFinished}
              radioButtonValue1={t("DA")}
              radioButtonValue2={t("NE")}
              onValueChange={handleNewDocumentOfReceive}
              labelText={t("Zaključena dispozicija")}
              control={control}
              errors={errors}
            />
            <div className="footer-buttons">
              <ButtonInput
                labelText={t("Sačuvaj")}
                onButtonClick={handleSubmit(onSubmit, onError)}
                addStyle={addButtonSaveStyle}
                disabled={activeUser?.role?.name === RoleType.Director}
              />
              <ButtonInput
                labelText={t("Resetuj")}
                onButtonClick={handleResetForm}
                addStyle={addButtonResetStyle}
                disabled={
                  disposition.loadingFinished === true ||
                  activeUser?.role?.name === RoleType.Director
                }
              />
            </div>
          </div>
        </div>
        <div className="request-details-dispositions">
          {processing ? (
            <ProgressSpinner />
          ) : (
            <div className="table-body">
              <DataTable
                scrollable={true}
                scrollHeight="80vh"
                sortMode="single"
                removableSort
                filters={filters}
                filterDisplay="row"
                globalFilterFields={[
                  "registrationNumber",
                  "dispositionNumberFormatted",
                  "driverName",
                  "price",
                  "inputLocation",
                  "outputLocation",
                ]}
                header={renderHeader()}
                emptyMessage="Nema kreiranih dispozicija"
                value={allDispositionsByRequest}
                showGridlines
                stripedRows
              >
                <Column
                  field="dispositionIdAtRequest"
                  sortable
                  style={{ minWidth: "150px" }}
                  header={t("Broj u zahtevu")}
                />
                <Column
                  field="dispositionNumberFormatted"
                  filter
                  header={t("Broj dispozicije")}
                />
                <Column
                  body={formatLoadingFinished}
                  filter
                  filterField={"loadingFinished"}
                  showFilterMenu={false}
                  filterElement={loadingFinishedRowFilterTemplate}
                  header={t("Zaključena dispozicija")}
                  style={{ minWidth: "50px" }}
                />
                <Column
                  body={formatDate}
                  header={t("Datum")}
                  style={{ minWidth: "100px" }}
                />
                <Column
                  body={formatVehicleType}
                  header={t("Tip vozila")}
                  style={{ minWidth: "100px" }}
                />
                <Column
                  field="registrationNumber"
                  filter
                  filterField={"registrationNumber"}
                  showFilterMenu={false}
                  style={{ minWidth: "150px" }}
                  header={t("Broj registracije")}
                />
                <Column
                  field="driverName"
                  filter
                  filterField={"driverName"}
                  showFilterMenu={false}
                  style={{ minWidth: "150px" }}
                  header={t("Vozač")}
                />
                <Column
                  field="price"
                  filter
                  filterField={"price"}
                  showFilterMenu={false}
                  header={t("Cena prevoza")}
                  style={{ minWidth: "80px" }}
                />
                {request?.type === RequestType.Export ? (
                  <Column
                    field="outputLocation"
                    filter
                    style={{ minWidth: "150px" }}
                    header={t("Mesto istovara")}
                  />
                ) : (
                  <Column
                    field="inputLocation"
                    filter
                    style={{ minWidth: "150px" }}
                    header={t("Mesto utovara")}
                  />
                )}
                <Column
                  body={formatOptions}
                  header={t("Opcije")}
                  style={{ minWidth: "200px" }}
                />
              </DataTable>
            </div>
          )}
          <ConfirmDialog
            visible={deleteConfirmVisible}
            onHide={() => setDeleteConfirmVisible(false)}
            message={t("Da li ste sigurni da želite da obrišete dispoziciju?")}
            header={t("Potvrda brisanja dispozicije")}
            icon="bi bi-exclamation-circle"
            acceptLabel={`${t("Da")}`}
            acceptClassName="accept-button"
            accept={accept}
            rejectLabel={`${t("Ne")}`}
            rejectClassName="reject-button"
            reject={() => {}}
          />
        </div>
      </div>
    </PageLayout>
  );
};
